import { GripVertical, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Fragment } from "react";
import { useLocalPref } from "@/client/hooks/use-local-pref";
import { currency, num, parseCurrencyInput } from "@/client/lib/format";
import { Button } from "../../ui/button";
import { Section } from "../view/section";
import { GridCellInput as CI } from "./form-field";
import type { FacilityCategory, FacilityFormRow } from "./types";
import { useRowDrag } from "./use-row-drag";

const SHOW_INFLATION_COLS_KEY = "insurance.showInflationCols";
const INFLATION_YEARS = [1, 2] as const;
const INFLATION_FIELDS: Record<
  (typeof INFLATION_YEARS)[number],
  { pct: keyof FacilityFormRow; amt: keyof FacilityFormRow }
> = {
  1: { pct: "inflationYear1Pct", amt: "calcInflationAmtYear1" },
  2: { pct: "inflationYear2Pct", amt: "calcInflationAmtYear2" },
};

interface Props {
  id: string;
  title: string;
  category: FacilityCategory;
  facilities: FacilityFormRow[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof FacilityFormRow, value: unknown) => void;
  gstEnabled: boolean;
}

const calc = "text-right text-xs tabular-nums bg-muted/10 whitespace-nowrap px-2";
const hCalc = "text-right bg-muted/30 text-[11px] whitespace-nowrap px-2";
const hInput = "text-right text-[11px] whitespace-nowrap px-1";

export function SectionFacilitiesForm({
  id,
  title,
  category,
  facilities,
  onAdd,
  onRemove,
  onUpdate,
  gstEnabled,
}: Props): React.ReactElement {
  const rows = facilities.map((row, index) => ({ row, index })).filter((r) => r.row.category === category);
  const showGst = gstEnabled || rows.some(({ row }) => row.addGst);
  const [showInflationCols, setShowInflationCols] = useLocalPref(SHOW_INFLATION_COLS_KEY, false);
  const emptyColSpan = 14 + (showGst ? 1 : 0) + (showInflationCols ? 4 : 0);
  const { dragOverIndex, getHandleProps, getRowDropProps } = useRowDrag<FacilityFormRow>({
    rows: rows.map(({ row }) => row),
    onUpdate: (pos, field, value) => {
      const target = rows[pos];
      if (target) onUpdate(target.index, field, value);
    },
  });

  const numCell = (field: keyof FacilityFormRow, value: number | null, i: number, w = "w-12"): React.ReactElement => (
    <td className="p-0.5">
      <CI value={value} onChange={(v) => onUpdate(i, field, v ? Number(v) : null)} w={w} />
    </td>
  );

  const totals = { area: 0, reinstatementIncl: 0, inflTotal: 0, demoCost: 0 };
  for (const { row: r } of rows) {
    totals.area += r.area ?? 0;
    totals.reinstatementIncl += r.calcReinstatementIncl ?? 0;
    totals.inflTotal += r.calcInflationTotal ?? 0;
    totals.demoCost += r.calcDemolitionCost ?? 0;
  }

  return (
    <Section id={id} title={title}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <p className="text-xs text-muted-foreground">Same calculation as Areas but without depreciation fields.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowInflationCols(!showInflationCols)}
          data-testid="toggle-inflation-columns"
        >
          {showInflationCols ? "Hide inflation" : "Show inflation"}
        </Button>
      </div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="w-7 px-1" />
              <th className={hInput} style={{ minWidth: 140 }}>
                Description
              </th>
              <th className={hInput}>Area</th>
              <th className={hInput}>Multiple</th>
              <th className={`${hCalc} border-l`}>Rate</th>
              {showGst && <th className={hInput}>GST</th>}
              <th className={hCalc}>Reinst Incl</th>
              <th className={hInput}>1st Mo</th>
              <th className={hInput}>Demo Mo</th>
              <th className={hInput}>Const Mo</th>
              <th className={hCalc}>Reinst Mo</th>
              {showInflationCols &&
                INFLATION_YEARS.map((y) => (
                  <Fragment key={y}>
                    <th className={hInput}>Inf Y{y}%</th>
                    <th className={hCalc}>Inf Y{y} $</th>
                  </Fragment>
                ))}
              <th className={hCalc}>Inf Total</th>
              <th className={hInput}>Demo %</th>
              <th className={hCalc}>Demo Cost</th>
              <th className="w-7" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={emptyColSpan} className="text-center text-muted-foreground py-8">
                  No rows added.
                </td>
              </tr>
            ) : (
              rows.map(({ row, index: i }, pos) => (
                <tr
                  key={row.tempId}
                  className={`border-b hover:bg-accent/30 ${dragOverIndex === pos ? "bg-accent/50" : ""}`}
                  {...getRowDropProps(pos)}
                >
                  <td className="px-1">
                    <button
                      type="button"
                      aria-label="Reorder row"
                      data-testid={`facility-row-drag-${row.tempId}`}
                      className="cursor-grab text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      {...getHandleProps(pos)}
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </button>
                  </td>
                  <td className="p-0.5">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) => onUpdate(i, "description", e.target.value)}
                      className="w-full h-7 rounded border border-input bg-white px-1.5 text-xs"
                    />
                  </td>
                  <td className="p-0.5">
                    <CI value={row.area} onChange={(v) => onUpdate(i, "area", v ? Number(v) : null)} />
                  </td>
                  <td className="p-0.5">
                    <CI value={row.multiple} onChange={(v) => onUpdate(i, "multiple", v ? Number(v) : null)} w="w-16" />
                  </td>
                  <td className={`${calc} border-l`}>
                    {row.rateOverride ? (
                      <span className="flex items-center gap-0.5 justify-end">
                        <CI
                          type="currency"
                          value={row.rate}
                          onChange={(v) => onUpdate(i, "rate", parseCurrencyInput(v))}
                          w="w-20"
                        />
                        <button
                          type="button"
                          onClick={() => onUpdate(i, "rateOverride", false)}
                          className="text-muted-foreground hover:text-primary"
                          title="Reset"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onUpdate(i, "rateOverride", true)}
                        className="hover:underline"
                        title="Click to override"
                      >
                        {num(row.calcRate ?? 0)}
                      </button>
                    )}
                  </td>
                  {showGst && (
                    <td className="p-0.5 text-center">
                      <input
                        type="checkbox"
                        checked={row.addGst}
                        onChange={(e) => onUpdate(i, "addGst", e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                    </td>
                  )}
                  <td className={`${calc} font-medium`}>{currency(row.calcReinstatementIncl)}</td>
                  {numCell("firstYearMonths", row.firstYearMonths, i)}
                  {numCell("demoPlanMonths", row.demoPlanMonths, i)}
                  {numCell("constructionMonths", row.constructionMonths, i)}
                  <td className={calc}>{num(row.calcReinstatementMonths ?? 0)}</td>
                  {showInflationCols &&
                    INFLATION_YEARS.map((y) => {
                      const { pct, amt } = INFLATION_FIELDS[y];
                      return (
                        <Fragment key={y}>
                          {numCell(pct, row[pct] as number | null, i)}
                          <td className={calc}>{currency(row[amt] as number | null)}</td>
                        </Fragment>
                      );
                    })}
                  <td className={`${calc} font-medium`}>{currency(row.calcInflationTotal)}</td>
                  {numCell("demolitionPercentage", row.demolitionPercentage, i)}
                  <td className={calc}>{currency(row.calcDemolitionCost)}</td>
                  <td className="px-1">
                    <button
                      type="button"
                      onClick={() => onRemove(i)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`remove-facility-${row.tempId}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
            {rows.length > 0 && (
              <tr className="border-t-2 bg-muted/20 font-medium">
                <td />
                <td className="px-2 py-1.5 text-xs">Totals</td>
                <td className="text-right text-xs tabular-nums px-2">{num(totals.area)}</td>
                <td colSpan={showGst ? 3 : 2} />
                <td className={calc}>{currency(totals.reinstatementIncl)}</td>
                <td colSpan={3} />
                <td />
                {showInflationCols && (
                  <>
                    <td />
                    <td />
                    <td />
                    <td />
                  </>
                )}
                <td className={calc}>{currency(totals.inflTotal)}</td>
                <td />
                <td className={calc}>{currency(totals.demoCost)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid={`add-facility-row-${id}`}>
          <Plus className="h-3.5 w-3.5" />
          Add Row
        </Button>
      </div>
    </Section>
  );
}
